import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { useBusinessHoursProps } from '../business-hours-props-context';
import { getWeekFromFirst } from '../model/regular-week/get-week-from-first';
import type { RegularWeekDay } from '../model/regular-week/regular-week';
import { useSettingsSpecialIntervals } from '../model/settings-special-interval/use-settings-special-intervals';
import { useSettingsWeek } from '../model/settings-week/use-settings-week';
import { getSpecialIntervalsFromDate } from '../model/special-interval/get-special-intervals-from-date';
import type { SpecialInterval } from '../model/special-interval/special-interval';

type ScheduleContextValue = {
  regularDays: RegularWeekDay[];
  specialIntervals: SpecialInterval[];
  inSpecialInterval: boolean;
  alignment: 'left' | 'center' | 'right';
};

const ScheduleContext = createContext<ScheduleContextValue>({
  regularDays: [],
  specialIntervals: [],
  inSpecialInterval: false,
  alignment: 'left'
});

export type ScheduleProviderProps = PropsWithChildren<{
  insidePopout?: boolean;
}>;

export function ScheduleProvider({
  children,
  insidePopout
}: ScheduleProviderProps) {
  const { firstWeekday, alignment } = useBusinessHoursProps();
  const week = useSettingsWeek();
  const settingsSpecialIntervals = useSettingsSpecialIntervals();

  const [inSpecialInterval, setInSpecialInterval] = useState(false);

  const regularDays = useMemo(
    () => getWeekFromFirst(week, firstWeekday),
    [week, firstWeekday]
  );

  const specialIntervals = useMemo(() => {
    const intervals = getSpecialIntervalsFromDate(
      settingsSpecialIntervals,
      new Date()
    );

    setInSpecialInterval(() => intervals.some(({ isToday }) => isToday));

    return intervals;
  }, [settingsSpecialIntervals]);

  const finalAlignment = insidePopout ? 'left' : alignment;

  return (
    <ScheduleContext.Provider
      value={{
        regularDays,
        specialIntervals,
        inSpecialInterval,
        alignment: finalAlignment
      }}
    >
      {children}
    </ScheduleContext.Provider>
  );
}

export const useScheduleContext = () => useContext(ScheduleContext);
